import { CheckResult, EstateCount } from './cbTeamOwnershipTransfer.types';

const CHECK_TITLES: Record<string, string> = {
  SourceOwnsTeam: 'Current owner has a team',
  SourceHasStripeCustomer: 'Current owner has billing set up',
  TargetIsBrandUser: 'New owner is a brand account',
  DistinctUsers: 'Current owner and new owner are different people',
  TargetOwnsNoTeam: 'New owner does not already own a team',
  TargetHasNoLiveSubscription: 'New owner has no active brand plan',
};

const ESTATE_LABELS: Record<string, string> = {
  teamMemberCleanup: 'Team member cleanup',
  crmMerge: 'Talent CRM records',
  paymentMethods: 'Saved payment methods',
  entitlements: 'Plan features / entitlements',
  balance: 'Wallet balance',
  team: 'Team',
  subscriptions: 'Subscriptions',
  invoices: 'Invoices',
  campaigns: 'Campaigns',
  myTalent: 'My Talent records',
  brandChatChannels: 'Brand chat channels',
  supportChatChannels: 'Support chat channels',
  billingAddress: 'Billing address',
  userStripePointers: 'Billing customer link',
};

const ROLE_LABELS: Record<string, string> = {
  agency: 'brand',
  admin: 'admin',
  athlete: 'athlete',
  contentcreator: 'content creator',
  organization: 'organization',
};

const humanizeToken = (value: string): string =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const formatCheckTitle = (check: CheckResult): string =>
  CHECK_TITLES[check.code] || check.title || humanizeToken(check.code);

export const formatCheckDetail = (check: CheckResult): string | null => {
  const detail = check.detail?.trim();
  if (!detail) {
    return null;
  }

  if (detail === 'no cb_team row for fromOwnerId') {
    return 'This brand account does not currently own a team.';
  }

  if (detail === 'user not found') {
    return 'No user was found for the selected account.';
  }

  if (
    detail ===
    'every registered user gets one at signup — this account is broken'
  ) {
    return 'This account is missing billing setup that every brand should have.';
  }

  if (detail.startsWith('role=')) {
    const role = detail.slice('role='.length).toLowerCase();
    const roleLabel = ROLE_LABELS[role] || role;
    return `Account type: ${roleLabel}.`;
  }

  if (detail.startsWith('owns team ')) {
    return 'This brand already owns a different team.';
  }

  if (
    detail.includes(':') &&
    /sub_|active|trialing|past_due|paused/i.test(detail)
  ) {
    const statuses = Array.from(
      new Set(
        detail
          .split(',')
          .map((part) => part.trim().split(':')[1])
          .filter(Boolean)
      )
    ).map((status) => status.replace(/_/g, ' '));

    return statuses.length
      ? `They already have a live brand plan (${statuses.join(', ')}).`
      : 'They already have a live brand plan.';
  }

  return detail;
};

export const formatEstateKey = (key: string): string =>
  ESTATE_LABELS[key] || humanizeToken(key);

export const formatEstateSummary = (estate: EstateCount[]): string => {
  const parts = estate
    .filter((item) => item.count > 0)
    .map((item) => `${item.count} ${formatEstateKey(item.key).toLowerCase()}`);

  if (!parts.length) {
    return 'Nothing would be moved for this transfer.';
  }

  return `This will move: ${parts.join(', ')}.`;
};

const isCheckLike = (
  item: unknown
): item is {
  code: string;
  title?: string;
  passed?: boolean;
  detail?: string | null;
} => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const maybeCheck = item as { code?: unknown };
  return typeof maybeCheck.code === 'string';
};

const normalizeChecks = (candidate: unknown): CheckResult[] | null => {
  if (!Array.isArray(candidate) || !candidate.every(isCheckLike)) {
    return null;
  }

  return candidate.map((item) => ({
    __typename: 'CbOwnershipCheckResult' as const,
    code: item.code,
    title: item.title || item.code,
    // Backend blocked payload is failedChecks — those are failures even if passed is omitted
    passed: typeof item.passed === 'boolean' ? item.passed : false,
    detail: item.detail ?? null,
  }));
};

/**
 * Backend throws CbOwnershipTransferBlockedException with ctx `{ failedChecks }`.
 * Nest/Apollo may surface that under a few extension shapes.
 */
export const extractChecksFromError = (
  error: unknown
): CheckResult[] | null => {
  const graphQLErrors =
    (
      error as {
        graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
      }
    )?.graphQLErrors || [];

  for (const graphQLError of graphQLErrors) {
    const extensions = graphQLError.extensions || {};
    const exception = extensions.exception as
      | Record<string, unknown>
      | undefined;
    const response = (extensions.response || exception?.response) as
      | Record<string, unknown>
      | undefined;
    const ctx = (extensions.ctx || exception?.ctx || response?.ctx) as
      | Record<string, unknown>
      | undefined;

    const candidates = [
      extensions.failedChecks,
      extensions.checks,
      ctx?.failedChecks,
      ctx?.checks,
      exception?.failedChecks,
      exception?.checks,
      response?.failedChecks,
      response?.checks,
    ];

    for (const candidate of candidates) {
      const checks = normalizeChecks(candidate);
      if (checks) {
        return checks;
      }
    }
  }

  return null;
};

/** Merge failed transfer checks into the existing preview list (keep passed ones). */
export const mergeFailedChecksIntoPreview = (
  currentChecks: CheckResult[],
  failedChecks: CheckResult[]
): CheckResult[] => {
  const failedByCode = new Map(
    failedChecks.map((check) => [check.code, { ...check, passed: false }])
  );

  const merged = currentChecks.map(
    (check) => failedByCode.get(check.code) || check
  );

  failedChecks.forEach((check) => {
    if (!merged.some((item) => item.code === check.code)) {
      merged.push({ ...check, passed: false });
    }
  });

  return merged;
};
